import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  IconButton, InputAdornment, ListSubheader, Menu, MenuItem, Popover, Select, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import {
  AddRounded as AddIcon, CloseRounded as RemoveIcon, ContentCopyOutlined as CopyIcon,
  ExpandMoreRounded as ExpandIcon, DeleteOutlineRounded as DeleteIcon,
} from '@mui/icons-material';
import PayrollService, {
  CommonSetup, PayBonusMetric, PayRule, PayRuleKind, PayRulePeriod, PaySetup, PayTemplate, Person, PersonWeek, RulePreview, StopKind, describeError,
} from '../../api/services/payroll.service';
import {
  Avatar, Eyebrow, RULE_HINT, RULE_KINDS_EARN, RULE_KINDS_OTHER, RULE_LABEL, count, money, newRule, shortDate, weekLabel,
} from './payrollUI';

// How one person is paid: a list of rules that add up.
//
// Each rule is a sentence with the numbers in it. The list starts from
// the company's defaults so setting somebody up is typing two numbers
// and removing what does not apply; "copy from" takes another person's
// whole list for the common case where the deal is the same. Every
// rule shows what it would have paid on a past week, so a change is
// checked before it is saved.

const STOP_KINDS: { key: StopKind; label: string }[] = [
  { key: 'pickup', label: 'Pickup' }, { key: 'delivery', label: 'Delivery' }, { key: 'ecommerce', label: 'eCommerce' },
];
const WEEKDAYS = [
  { n: 6, label: 'Sat' }, { n: 0, label: 'Sun' }, { n: 1, label: 'Mon' }, { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' }, { n: 4, label: 'Thu' }, { n: 5, label: 'Fri' },
];

const withFreshIds = (rules: PayRule[]): PayRule[] => rules.map((r) => ({ ...newRule(r.kind), ...r, id: newRule(r.kind).id }));

const Money: React.FC<{ value: number | null; onChange: (v: number | null) => void; label: string; step?: string; width?: number }> = ({
  value, onChange, label, step = '0.01', width = 112,
}) => {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  useEffect(() => { setDraft(value === null ? '' : String(value)); }, [value]);
  return (
    <TextField
      size="small"
      placeholder="0.00"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const n = draft.trim() === '' ? null : Number(draft);
        if (n !== null && (!Number.isFinite(n) || n < 0)) { setDraft(value === null ? '' : String(value)); return; }
        if (n !== value) onChange(n);
      }}
      inputProps={{ 'aria-label': label, inputMode: 'decimal', step, style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, padding: '6px 8px' } }}
      InputProps={{ startAdornment: <InputAdornment position="start" sx={{ mr: 0.25 }}>$</InputAdornment> }}
      sx={{ width, mx: 0.5, verticalAlign: 'middle', '& .MuiOutlinedInput-root': { bgcolor: value === null ? 'warning.light' : 'primary.light' } }}
    />
  );
};

const Num: React.FC<{ value: number | null; onChange: (v: number | null) => void; label: string; width?: number }> = ({
  value, onChange, label, width = 72,
}) => {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  useEffect(() => { setDraft(value === null ? '' : String(value)); }, [value]);
  return (
    <TextField
      size="small"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const n = draft.trim() === '' ? null : Number(draft);
        if (n !== null && (!Number.isFinite(n) || n < 0)) { setDraft(value === null ? '' : String(value)); return; }
        if (n !== value) onChange(n);
      }}
      inputProps={{ 'aria-label': label, inputMode: 'numeric', style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, padding: '6px 8px' } }}
      sx={{ width, mx: 0.5, verticalAlign: 'middle', '& .MuiOutlinedInput-root': { bgcolor: 'primary.light' } }}
    />
  );
};

const Word: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ display: 'inline-block', bgcolor: 'grey.100', px: 1, py: 0.25, borderRadius: 1.5, fontWeight: 600, mx: 0.25 }}>{children}</Box>
);

const Sentence: React.FC<{ rule: PayRule; onChange: (patch: Partial<PayRule>) => void }> = ({ rule, onChange }) => {
  const amount = <Money label={`${RULE_LABEL[rule.kind]} amount`} value={rule.amount} onChange={(amount) => onChange({ amount })} />;
  switch (rule.kind) {
    case 'day_rate': return <>Pay {amount} for every <Word>day worked</Word></>;
    case 'per_stop': return <>Pay {amount} per <Word>stop</Word></>;
    case 'over_threshold':
      return (
        <>
          Pay {amount} per stop above <Num label="Threshold" value={rule.threshold ?? null} onChange={(threshold) => onChange({ threshold })} /> counted
          <Select size="small" value={rule.period || 'day'} onChange={(e) => onChange({ period: e.target.value as PayRulePeriod })} sx={{ mx: 0.5, fontWeight: 600, '& .MuiSelect-select': { py: 0.6 } }}>
            <MenuItem value="day">each day</MenuItem>
            <MenuItem value="week">for the whole week</MenuItem>
            <MenuItem value="pooled">each day, pooled over the week</MenuItem>
          </Select>
        </>
      );
    case 'hourly': return <>Pay {amount} per <Word>hour</Word></>;
    case 'weekly_salary': return <>Pay {amount} a <Word>week</Word>, fixed</>;
    case 'training_day': return <>Pay {amount} for every <Word>training day</Word></>;
    case 'vacation_day': return <>Pay {amount} for every <Word>vacation day</Word></>;
    case 'bonus':
      return (
        <>
          Add {amount} when
          <Select size="small" value={rule.metric || 'days'} onChange={(e) => onChange({ metric: e.target.value as PayBonusMetric })} sx={{ mx: 0.5, fontWeight: 600, '& .MuiSelect-select': { py: 0.6 } }}>
            <MenuItem value="days">days worked in the week</MenuItem>
            <MenuItem value="stops">stops in the week</MenuItem>
            <MenuItem value="packages">packages in the week</MenuItem>
            <MenuItem value="stops_per_day">a day's stops</MenuItem>
          </Select>
          reach <Num label="Bonus target" value={rule.threshold ?? null} onChange={(threshold) => onChange({ threshold })} />
        </>
      );
    case 'deduction':
      return (
        <>
          Take {amount} every <Word>week</Word> for
          <TextField size="small" placeholder="van rent" value={rule.label || ''} onChange={(e) => onChange({ label: e.target.value })} inputProps={{ 'aria-label': 'Deduction label', style: { padding: '6px 8px', fontWeight: 600 } }} sx={{ width: 150, mx: 0.5, verticalAlign: 'middle' }} />
        </>
      );
    default: return null;
  }
};

const RuleEditor: React.FC<{
  person: Person;
  people: Person[];
  onSaved: (person: Person) => void;
}> = ({ person, people, onSaved }) => {
  const [rules, setRules] = useState<PayRule[]>([]);
  const [fedexId, setFedexId] = useState(person.fedexId);
  const [stopKinds, setStopKinds] = useState<StopKind[]>(person.stopKinds);
  const [daysThatCount, setDaysThatCount] = useState<number[]>(person.daysThatCount);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [preview, setPreview] = useState<RulePreview | null>(null);
  const [previewWeek, setPreviewWeek] = useState<string>('');
  const [history, setHistory] = useState<PersonWeek[]>([]);
  const [more, setMore] = useState(false);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [copyAnchor, setCopyAnchor] = useState<HTMLElement | null>(null);
  // Templates: saved ones and the setups people already have. Picking one
  // copies it into the form; nothing stays linked.
  const [templates, setTemplates] = useState<{ saved: PayTemplate[]; common: CommonSetup[] }>({ saved: [], common: [] });
  const [templateAnchor, setTemplateAnchor] = useState<HTMLElement | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateBusy, setTemplateBusy] = useState(false);
  const [askFill, setAskFill] = useState(false);
  const loadTemplates = useCallback(() => { PayrollService.templates().then(setTemplates).catch(() => undefined); }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  const previewTimer = useRef<number | null>(null);
  const lastAccount = useRef<string | null>(null);

  // A person with nothing yet starts from the company's defaults.
  useEffect(() => {
    let active = true;
    setFedexId(person.fedexId);
    setStopKinds(person.stopKinds);
    setDaysThatCount(person.daysThatCount);
    setDirty(false);
    setError(null);
    // A save refreshes the same person; only a different person clears
    // the "Saved" line, or it vanishes the instant it appears.
    if (lastAccount.current !== person.accountId) { setSaved(null); setPreviewWeek(''); }
    lastAccount.current = person.accountId;
    if (person.profileId && person.rules.length) {
      setRules(person.rules);
    } else {
      PayrollService.settings().then((s) => { if (active) setRules(withFreshIds(s.defaultRules)); }).catch(() => { if (active) setRules([]); });
    }
    PayrollService.personHistory(person.accountId, 12).then((h) => { if (active) setHistory(h); }).catch(() => undefined);
    return () => { active = false; };
  }, [person.accountId, person.profileId, person.rules, person.fedexId, person.stopKinds, person.daysThatCount]);

  const runPreview = useCallback((next: PayRule[], kinds: StopKind[], days: number[], week: string) => {
    if (previewTimer.current) window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(async () => {
      try {
        setPreview(await PayrollService.previewRules(person.accountId, { rules: next, stopKinds: kinds, daysThatCount: days, weekEnd: week || undefined }));
      } catch { setPreview(null); }
    }, 350);
  }, [person.accountId]);

  useEffect(() => { runPreview(rules, stopKinds, daysThatCount, previewWeek); }, [rules, stopKinds, daysThatCount, previewWeek, runPreview]);

  const update = (id: string, patch: Partial<PayRule>) => {
    setRules((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const remove = (id: string) => { setRules((rs) => rs.filter((r) => r.id !== id)); setDirty(true); };
  const add = (kind: PayRuleKind) => { setRules((rs) => [...rs, newRule(kind)]); setAddAnchor(null); setDirty(true); };
  const startFrom = (setup: PaySetup) => {
    setRules(withFreshIds(setup.rules));
    if (setup.stopKinds?.length) setStopKinds(setup.stopKinds);
    setDaysThatCount(setup.daysThatCount || []);
    setCopyAnchor(null);
    setDirty(true);
  };
  const copyFrom = (other: Person) => startFrom({ ...other, stopKinds: other.stopKinds });

  const saveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) return;
    setTemplateBusy(true);
    setError(null);
    try {
      await PayrollService.saveTemplate({ name, rules, stopKinds, daysThatCount });
      setTemplateAnchor(null);
      setTemplateName('');
      setSaved(`Saved as the template "${name}".`);
      loadTemplates();
    } catch (err) {
      setError(describeError(err, 'The template could not be saved.'));
    } finally {
      setTemplateBusy(false);
    }
  };
  const removeTemplate = async (t: PayTemplate) => {
    try { await PayrollService.deleteTemplate(t.id); loadTemplates(); } catch (err) { setError(describeError(err, 'The template could not be deleted.')); }
  };

  const usable = rules.some((r) => r.enabled !== false && r.amount !== null && r.amount !== undefined);
  const unpaid = person.unpaidFinalizedWeeks || 0;
  // A person paid nothing in finalized weeks (they had no rules then) is
  // asked whether these rules should fill those weeks too.
  const requestSave = () => { if (usable && unpaid > 0) setAskFill(true); else save(false); };

  const save = async (fillFinalized: boolean) => {
    setAskFill(false);
    setSaving(true);
    setError(null);
    try {
      const result = await PayrollService.saveProfile(person.accountId, {
        rules, fedexId, stopKinds, daysThatCount, ...(fillFinalized && { fillFinalized: true }),
      });
      setDirty(false);
      const parts = [result.refreshedDrafts ? 'Draft weeks were recalculated' : 'Applies from the next upload'];
      if (result.filledFinalized) parts.push(`${result.filledFinalized} finalized ${result.filledFinalized === 1 ? 'week was' : 'weeks were'} filled in`);
      setSaved(`Saved. ${parts.join('; ')}.`);
      onSaved(result);
    } catch (err) {
      setError(describeError(err, 'The rules could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setRules(person.profileId && person.rules.length ? person.rules : rules);
    setFedexId(person.fedexId);
    setStopKinds(person.stopKinds);
    setDaysThatCount(person.daysThatCount);
    setDirty(false);
  };

  const previewByRule = useMemo(() => new Map((preview?.receipt || []).map((r) => [r.ruleId, r])), [preview]);
  const copyCandidates = people.filter((p) => p.accountId !== person.accountId && p.hasRules);

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
        <Avatar name={person.name} size={44} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h3" sx={{ lineHeight: 1.2 }}>{person.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {person.role}
          </Typography>
        </Box>
        <TextField
          size="small"
          label="FedEx ID"
          value={fedexId}
          onChange={(e) => { setFedexId(e.target.value.replace(/\D/g, '')); setDirty(true); }}
          inputProps={{ inputMode: 'numeric', maxLength: 12, style: { fontVariantNumeric: 'tabular-nums' } }}
          sx={{ width: 170 }}
        />
        <Button variant="outlined" startIcon={<CopyIcon />} onClick={(e) => setCopyAnchor(e.currentTarget)}>Pay templates</Button>
        <Menu open={!!copyAnchor} anchorEl={copyAnchor} onClose={() => setCopyAnchor(null)} slotProps={{ paper: { sx: { maxHeight: 480, width: 340 } } }}>
          <ListSubheader sx={{ lineHeight: '32px' }}>Saved templates</ListSubheader>
          {templates.saved.length === 0 && (
            <MenuItem disabled sx={{ whiteSpace: 'normal' }}><Typography variant="caption">None yet. Set somebody up, then "Save as template".</Typography></MenuItem>
          )}
          {templates.saved.map((t) => (
            <MenuItem key={t.id} onClick={() => startFrom(t)} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', display: 'block' }}>{t.description}</Typography>
              </Box>
              <IconButton size="small" aria-label={`Delete the template ${t.name}`} onClick={(e) => { e.stopPropagation(); removeTemplate(t); }}><DeleteIcon fontSize="small" /></IconButton>
            </MenuItem>
          ))}
          {templates.common.length > 0 && <ListSubheader sx={{ lineHeight: '32px' }}>Common setups in this company</ListSubheader>}
          {templates.common.map((c) => (
            <MenuItem key={c.description + c.people} onClick={() => startFrom(c)}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{c.description}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, whiteSpace: 'nowrap' }}>{c.people} {c.people === 1 ? 'person' : 'people'}</Typography>
            </MenuItem>
          ))}
          {copyCandidates.length > 0 && <ListSubheader sx={{ lineHeight: '32px' }}>Copy someone's pay</ListSubheader>}
          {copyCandidates.map((p) => (
            <MenuItem key={p.accountId} onClick={() => copyFrom(p)}>
              <Box><Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography><Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', display: 'block' }}>{p.description}</Typography></Box>
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {saved && !dirty && <Alert severity="success" onClose={() => setSaved(null)}>{saved}</Alert>}

      <Stack gap={1.25}>
        {rules.map((rule) => {
          const line = previewByRule.get(rule.id);
          return (
            <Card key={rule.id} sx={{ p: 1.75, pl: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderColor: rule.amount === null ? 'warning.main' : 'divider' }}>
              <Box sx={{ flex: 1, minWidth: 0, lineHeight: 2.4, fontSize: 15 }}>
                <Sentence rule={rule} onChange={(patch) => update(rule.id, patch)} />
              </Box>
              <Box sx={{ textAlign: 'right', minWidth: 96 }}>
                <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: !line || line.incomplete || line.amount === 0 ? 'text.disabled' : 'text.primary' }}>
                  {line && !line.incomplete ? money(line.amount, { cents: true }) : '—'}
                </Typography>
              </Box>
              <Tooltip title="Remove this rule">
                <IconButton size="small" aria-label={`Remove ${RULE_LABEL[rule.kind]}`} onClick={() => remove(rule.id)}><RemoveIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Card>
          );
        })}
        <Button variant="outlined" startIcon={<AddIcon />} onClick={(e) => setAddAnchor(e.currentTarget)} sx={{ borderStyle: 'dashed', justifyContent: 'flex-start', py: 1.25 }}>
          Add a rule
        </Button>
        <Menu open={!!addAnchor} anchorEl={addAnchor} onClose={() => setAddAnchor(null)} slotProps={{ paper: { sx: { width: 360 } } }}>
          <ListSubheader>Earn</ListSubheader>
          {RULE_KINDS_EARN.map((k) => (
            <MenuItem key={k} onClick={() => add(k)}>
              <Box><Typography variant="body2" sx={{ fontWeight: 600 }}>{RULE_LABEL[k]}</Typography><Typography variant="caption" color="text.secondary">{RULE_HINT[k]}</Typography></Box>
            </MenuItem>
          ))}
          <ListSubheader>Add or take</ListSubheader>
          {RULE_KINDS_OTHER.map((k) => (
            <MenuItem key={k} onClick={() => add(k)}>
              <Box><Typography variant="body2" sx={{ fontWeight: 600 }}>{RULE_LABEL[k]}</Typography><Typography variant="caption" color="text.secondary">{RULE_HINT[k]}</Typography></Box>
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      <Card sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Eyebrow>Try it on a past week</Eyebrow>
          {preview?.weeks?.length ? (
            <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
              <Select size="small" value={previewWeek || preview.weeks[0]} onChange={(e) => setPreviewWeek(String(e.target.value))}>
                {preview.weeks.map((w) => <MenuItem key={w} value={w}>Week ending {shortDate(w)}</MenuItem>)}
              </Select>
              <Typography variant="caption" color="text.secondary">
                {preview.totals.days} {preview.totals.days === 1 ? 'day' : 'days'}, {count(preview.totals.stops)} stops
              </Typography>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">No weeks yet</Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">These rules</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'primary.dark', lineHeight: 1.1 }}>{preview ? money(preview.calculated, { cents: true }) : '—'}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">Was paid</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'text.secondary', lineHeight: 1.1 }}>{preview?.paid != null ? money(preview.paid, { cents: true }) : '—'}</Typography>
        </Box>
        <Stack direction="row" gap={1} sx={{ ml: 'auto' }}>
          <Button onClick={(e) => setTemplateAnchor(e.currentTarget)} disabled={!rules.length}>Save as template</Button>
          <Button onClick={discard} disabled={!dirty || saving}>Discard</Button>
          <Button variant="contained" disableElevation onClick={requestSave} disabled={(!dirty && !(usable && unpaid)) || saving}>Save rules</Button>
        </Stack>
        <Popover open={!!templateAnchor} anchorEl={templateAnchor} onClose={() => setTemplateAnchor(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Stack component="form" gap={1.25} sx={{ p: 2, width: 300 }} onSubmit={(e: React.FormEvent) => { e.preventDefault(); saveAsTemplate(); }}>
            <TextField autoFocus size="small" label="Template name" placeholder="Day rate + over 160" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
            <Typography variant="caption" color="text.secondary">A copy of these rules to start somebody else from. Changing it later changes nobody.</Typography>
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button size="small" onClick={() => setTemplateAnchor(null)}>Cancel</Button>
              <Button size="small" type="submit" variant="contained" disableElevation disabled={!templateName.trim() || templateBusy}>Save template</Button>
            </Stack>
          </Stack>
        </Popover>
        <Dialog open={askFill} onClose={() => setAskFill(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Fill in finalized weeks too?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {person.name} is on {unpaid} finalized {unpaid === 1 ? 'week' : 'weeks'} with no pay rules, so TopOps paid them nothing there.
              These rules can fill {unpaid === 1 ? 'that week' : 'those weeks'} in. Weeks they were already paid in never change.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => save(false)}>Drafts only</Button>
            <Button variant="contained" disableElevation onClick={() => save(true)}>Fill {unpaid} finalized {unpaid === 1 ? 'week' : 'weeks'}</Button>
          </DialogActions>
        </Dialog>
      </Card>

      <Box>
        <Button size="small" endIcon={<ExpandIcon sx={{ transform: more ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />} onClick={() => setMore((v) => !v)} sx={{ color: 'text.secondary' }}>
          More options
        </Button>
        <Collapse in={more}>
          <Card sx={{ p: 2, mt: 1 }}>
            <Stack gap={2}>
              <Box>
                <Eyebrow>Stops that count</Eyebrow>
                <Stack direction="row" flexWrap="wrap">
                  {STOP_KINDS.map((k) => (
                    <FormControlLabel
                      key={k.key}
                      control={<Checkbox size="small" checked={stopKinds.includes(k.key)} onChange={(e) => {
                        const next = e.target.checked ? [...stopKinds, k.key] : stopKinds.filter((x) => x !== k.key);
                        if (!next.length) return;
                        setStopKinds(next); setDirty(true);
                      }} />}
                      label={k.label}
                    />
                  ))}
                </Stack>
              </Box>
              <Divider />
              <Box>
                <Eyebrow>Days that count</Eyebrow>
                <ToggleButtonGroup
                  size="small"
                  value={daysThatCount.length ? daysThatCount : WEEKDAYS.map((d) => d.n)}
                  onChange={(_, next: number[]) => {
                    if (!next.length) return;
                    setDaysThatCount(next.length === 7 ? [] : next); setDirty(true);
                  }}
                  sx={{ mt: 0.5, flexWrap: 'wrap' }}
                >
                  {WEEKDAYS.map((d) => <ToggleButton key={d.n} value={d.n} sx={{ px: 1.5 }}>{d.label}</ToggleButton>)}
                </ToggleButtonGroup>
              </Box>
            </Stack>
          </Card>
        </Collapse>
      </Box>

      {history.length > 0 && (
        <Box>
          <Eyebrow>Recent weeks</Eyebrow>
          <Card sx={{ mt: 0.5 }}>
            {history.map((w, i) => (
              <Stack key={w._id} direction="row" alignItems="center" gap={2} sx={{ px: 2, py: 1.25, borderTop: i ? '1px solid' : 0, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                  {w.weekStart ? weekLabel(w.weekStart, w.weekEnd) : `Week ending ${shortDate(w.weekEnd)}`}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{w.runStatus === 'draft' ? 'draft' : ''}</Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{w.totals.days} days · {count(w.totals.stops)} stops</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 90, textAlign: 'right' }}>{money(w.total, { cents: true })}</Typography>
              </Stack>
            ))}
          </Card>
        </Box>
      )}
    </Stack>
  );
};

export default RuleEditor;
